import { Flex, GapSize } from "@/components/atoms/flex";
import { useStyle } from "./style";
import { Call20Filled } from "@fluentui/react-icons";
import { mergeClasses, tokens } from "@fluentui/react-components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Dialpad, DialpadItem } from "@/components/molecules/dialpad";
import {
	azurePresence,
	searchContactsByPhone,
	sendTone,
} from "@/repository/queryFns";
import {
	OutboundCallRequest,
	TargetSelectionSource,
	TrustedEndpoint,
} from "@/repository/models/outboundCall";
import { Select, SelectItemProps } from "@/components/molecules/select";
import {
	formatSipOrPhoneNumber,
	isValidEmail,
	isValidPhoneNumber,
	isValidToneKeyNumber,
} from "@/common/helpers/validationHelper";
import { StorageKeys } from "@/common/constants/storageKeys";
import { Lockr } from "@/common/Lockr";
import {
	CurrentInteractionActions,
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { useMessage } from "@/common/messages";
import { PresenceDto, ShortContactInfo } from "@/repository/models";
import { PresenceState } from "@/common/constants/presence";
import { useVoiceOutbound } from "@/hooks/outbound";
import {
	AppCacheActions,
	AppCacheState,
	AppSettingsState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import {
	ContactItemData,
	Multiselect,
	OutboundType,
} from "@/components/molecules/multiselect";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import { CallTargetType } from "@/common/types";
import {
	NavigationActions,
	useNavigationStore,
	TabType,
} from "@/common/stores/persistingStores/navigation";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import { AcsClientType, PlatformType } from "@/common/constants/shared";
import { msalConfig } from "@/common/authentication/msalConfig";
import { embededInTeams } from "@/common/authentication/authenticationUtils";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";

export const OutboundVoiceInteraction = () => {
	const classes = useStyle();
	const intl = useIntl();

	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);
	const [selectedEndpoint, setEndpoint] = useState<TrustedEndpoint>();
	const [isCallButtonActive, setCallButtonState] = useState<boolean>(false);
	const [isViewEnabled, setIsViewEnabled] = useState<boolean>(false);
	const { show } = useMessage();
	const hideInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionActions) => state.hideNew
	);

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const setSelectedTab = useNavigationStore(
		(state: NavigationActions) => state.setSelectedTab
	);

	const newInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.new
	);

	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);

	const [inputValue, setInputValue] = useState<string>("");
	const [isInputValueValid, setIsInputValueValid] = useState<boolean>(true);
	const [destination, setDestination] = useState<ContactItemData[]>([]);

	const {
		trustedEndpoints,
		initCallAction,
		isOutboundPossible,
		isOutboundAllowed,
		isUnifyInteractionOnHold,
	} = useVoiceOutbound();
	const cacheState = useAppCacheStore(
		(state: AppCacheState & AppCacheActions) => state
	);

	const onDialpadClickCallback = useCallback(
		(data: DialpadItem) => {
			setInputValue((prev: string) => prev + data.digit);
		},
		[setInputValue, inputValue]
	);

	const onDialpadInACallClick = (data: DialpadItem) => {
		if (!isValidToneKeyNumber(data.digit.toString()) || !callSession) {
			return;
		}

		if (!callSession.transferTarget || !callSession.transferTarget.name) {
			sendTone(data.digit);
		}
	};

	const isInACall = () => {
		return (
			callSession &&
			callSession.state &&
			callSession.state !== AgentInteractionStatus.Terminated
		) || false;
	};

	const dialpadClickCallback = (data: DialpadItem) => {
		return isInACall()
			? onDialpadInACallClick(data)
			: onDialpadClickCallback(data);
	};

	useEffect(() => {
		if (
			!appSettings.callRememberLastOutboundResourceAccount ||
			!trustedEndpoints?.length
		) {
			return;
		}

		const cachedEndpoint = trustedEndpoints.find(
			(x) => buildEndpointKey(x) === cacheState.cache[CacheKey.OutboundEndpoint]
		);

		if (!cachedEndpoint) {
			cacheState.removeCaches([CacheKey.OutboundEndpoint]);
			return;
		}

		setEndpoint(
			!cachedEndpoint.acsResourceId && isUnifyInteractionOnHold
				? undefined
				: cachedEndpoint
		);
	}, [trustedEndpoints, isUnifyInteractionOnHold]);

	useEffect(
		() =>
			setCallButtonState(
				(!!inputValue.length || !!destination.length) &&
					!!selectedEndpoint &&
					isOutboundPossible &&
					isOutboundAllowed
			),
		[inputValue, selectedEndpoint, isOutboundPossible, isOutboundAllowed]
	);

	useEffect(
		() => setIsViewEnabled(isOutboundPossible && isOutboundAllowed),
		[isOutboundPossible, isOutboundAllowed]
	);

	useEffect(() => {
		if (!isOutboundAllowed) {
			onInputClear();
		}
	}, [isOutboundAllowed]);

	useEffect(() => {
		if (!!newInteraction?.data) {
			setDestination([
				{
					name: newInteraction.data.name,
					value: newInteraction.data.value,
					contactId: newInteraction.data.contactId,
					selectionSource:
						newInteraction.data.selectionSource ??
						TargetSelectionSource.Invalid,
					selectionType: newInteraction.data.selectionType,
					selectionInteractionHistoryId:
						newInteraction.data.selectionInteractionHistoryId,
				},
			]);

			setInputValue(newInteraction.data.value);
		}
	}, [newInteraction?.data]);

	useEffect(() => {
		if (
			appSettings.callRememberLastOutboundResourceAccount &&
			!!newInteraction?.data &&
			trustedEndpoints.length == 1
		) {
			setEndpoint(trustedEndpoints[0]);
		}
	}, []);

	const initCallMutation = useCallback(
		(request: OutboundCallRequest) => {
			setCallButtonState(false);
			initCallAction(request)
				.then(() => {
					if (!appSettings.callRememberLastOutboundResourceAccount) {
						setEndpoint(undefined);
					}
					hideInteraction();
				})
				.catch((error) => {
					captureError(error, "Outbound call failed:");
					setCallButtonState(true);
				});
		},
		[initCallAction]
	);

	const onCallInitCallback = useCallback(() => {
		if (!isOutboundPossible || !isOutboundAllowed || !selectedEndpoint) {
			return;
		}

		if (!isValidPhoneNumber(inputValue) && !isValidEmail(inputValue)) {
			setIsInputValueValid(false);
			return;
		} else {
			setIsInputValueValid(true);
		}

		if (!selectedEndpoint.objectId) {
			show("global", {
				text: intl.formatMessage({
					id: "Interaction.New.NoObjectId",
				}),
				intent: "error",
			});
		}

		setInputValue("");

		azurePresence().then((presence: PresenceDto) => {
			if (
				presence.availability === PresenceState.DND ||
				presence.activity === PresenceState.DND
			) {
				show("global", {
					text: intl.formatMessage({
						id: "Interaction.New.InvalidStatusForCall",
					}),
					intent: "error",
				});

				return;
			}

			const inputValueDestination = {
				value: inputValue,
				name: "",
				selectionSource: TargetSelectionSource.Manual,
			} as ContactItemData;

			const targetDestination =
				destination[0] && destination[0].value === inputValueDestination.value
					? destination[0].value
					: inputValueDestination.value;

			const requestData: OutboundCallRequest = {
				agent: {
					agentId: Lockr.get(StorageKeys.UserId) as string,
					agentUpn: localStorage.getItem(StorageKeys.UPN) as string,
					tenantId: Lockr.get(StorageKeys.CompanyId) as string,
					azureTenantId: localStorage.getItem(
						StorageKeys.TIDForAzure
					) as string,
				},
				target: {
					target: targetDestination,
					isPstn: isValidPhoneNumber(targetDestination),
					contactId: destination[0]?.contactId,
					contactName: destination[0]?.name,
					selectionSource:
						destination[0]?.selectionSource ??
						inputValueDestination.selectionSource,
					selectionType: destination[0]?.selectionType,
					selectionInteractionHistoryId:
						destination[0]?.selectionInteractionHistoryId,
				},
				selectedEndpoint: selectedEndpoint,
				platformType:
					selectedEndpoint.applicationId == msalConfig.auth.clientId
						? PlatformType.Acs
						: PlatformType.Graph,
				scenarioId: null,
				serverSideCall:
					embededInTeams || appSettings.acsClientType == AcsClientType.Teams
						? true
						: false, // outbound call will be started client side, when agent is using browser and persona setting is not set to Teams
				skipInitCallSession: !!callSession && isInACall(), // skip call session creation in backend, when there is already an active call session (unify calls)
				isAgent: false,
			};

			addBreadcrumb("ui_interaction", "Outbound call init", requestData);

			setSelectedTab(TabType.Interactions);
			appStoreActions.removeCaches([CacheKey.SessionVariableText]);
			initCallMutation(requestData);

			setDestination([]);
		});
	}, [
		initCallMutation,
		selectedEndpoint,
		destination,
		isOutboundPossible,
		isOutboundAllowed,
		inputValue,
	]);

	const buildEndpointKey = (endpoint?: TrustedEndpoint) =>
		!!endpoint ? `${endpoint.id}|${endpoint.queueId}` : undefined;

	const buildEndpointName = (endpoint: TrustedEndpoint) => {
		const endpointInfo = [
			endpoint.name,
			endpoint.phoneNumber,
			!endpoint.phoneNumber ? endpoint.applicationName : undefined,
		]
			.filter((x) => !!x)
			.join(", ");

		return `(${endpointInfo})`;
	};

	const endpointsSelectItems = useMemo<SelectItemProps[]>(
		() =>
			trustedEndpoints?.map((endpoint: TrustedEndpoint) => ({
				key: buildEndpointKey(endpoint),
				name: buildEndpointName(endpoint),
				queueName: endpoint.queueName,
				data: endpoint,
				isDisabledExceptUnify: isUnifyInteractionOnHold,
			})) ?? [],
		[trustedEndpoints]
	);

	const onEndpointSelectedCallback = useCallback(
		(item: SelectItemProps) => {
			if (!trustedEndpoints?.length || !item.data) {
				return;
			}

			setEndpoint(item.data);
			cacheState.setCache(CacheKey.OutboundEndpoint, item.key);
		},
		[trustedEndpoints]
	);

	const getContactSearchListItem = (
		contact: ShortContactInfo,
		header: string,
		value: string,
		key: string,
		selectionType: CallTargetType
	) => {
		const result = {
			key: key,
			header: header,
			secondaryHeader: value,
			content: [contact.jobTitle, contact.department]
				.filter((x) => !!x)
				.join("/"),
			data: {
				name: contact.name,
				value: value,
				contactId: contact.id,
				selectionSource: TargetSelectionSource.TopBarSearch,
				selectionType: selectionType,
			},
		};

		return result;
	};

	const getContactSearchListByAllPhoneNumbers = (contact: ShortContactInfo) => {
		return [
			getContactSearchListItem(
				contact,
				contact.name,
				contact.mobilePhone,
				`mobile-phone-${contact.id}`,
				CallTargetType.Mobile
			),
			getContactSearchListItem(
				contact,
				contact.name,
				contact.businessPhone,
				`business-phone-${contact.id}`,
				CallTargetType.Bussiness
			),
		];
	};

	const getContactSearchItemByPhoneNumber = (contact: ShortContactInfo) => {
		const selectionType: CallTargetType =
			contact.mobilePhone.length > 0
				? CallTargetType.Mobile
				: CallTargetType.Bussiness;
		return getContactSearchListItem(
			contact,
			contact.name,
			selectionType == CallTargetType.Mobile
				? contact.mobilePhone
				: contact.businessPhone,
			contact.id,
			selectionType
		);
	};

	const mapContactsToSearchList = (contacts: ShortContactInfo[]) => {
		var result = contacts.flatMap((contact) =>
			contact.mobilePhone.length > 0 && contact.businessPhone.length > 0
				? getContactSearchListByAllPhoneNumbers(contact)
				: getContactSearchItemByPhoneNumber(contact)
		);
		return result;
	};

	const onInputPaste: React.ClipboardEventHandler = useCallback(
		(event) => {
			const formatedValue = formatSipOrPhoneNumber(
				event.clipboardData!.getData("text")
			);

			event.preventDefault();

			setInputValue(formatedValue!);
		},
		[setInputValue]
	);

	const onInputClear = () => {
		setInputValue("");
		setDestination([]);
	};

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (
				e.key === "Enter" &&
				(destination.length > 0 || inputValue.length > 0)
			) {
				onCallInitCallback();
			}
		};
		document.addEventListener("keydown", handler);
		return () => {
			document.removeEventListener("keydown", handler);
		};
	}, [destination, selectedEndpoint, inputValue]);

	return (
		<div>
			<div className={classes.root}>
				<Flex
					gap={GapSize.Medium}
					className={classes.rightContainer}
					vAlign="center"
				>
					<Select
						selectedKey={buildEndpointKey(selectedEndpoint)}
						listHeader={intl.formatMessage({
							id: "Interaction.New.CallFromQueue",
						})}
						unselectedMessage={intl.formatMessage({
							id: "Interaction.New.MandatoryQueue",
						})}
						items={endpointsSelectItems}
						onSelect={onEndpointSelectedCallback}
						classNames={{
							trigger: classes.selectTrigger,
							container: classes.selectContainer,
							menuPopoverOutbound: classes.menuPopoverOutbound,
						}}
						disabled={!isViewEnabled}
					/>
				</Flex>
				<Flex
					gap={GapSize.Medium}
					className={classes.leftContainer}
					vAlign="center"
				>
					<Multiselect
						autoFocus
						limit={1}
						value={inputValue}
						placeholder={intl.formatMessage({
							id: "Interaction.New.InputPlaceholder",
						})}
						items={[]}
						classesName={{
							root: mergeClasses(
								classes.multiInput,
								!isInputValueValid ? classes.multiInputInvalid : ""
							),
							contactLabel: classes.contactLabel,
							container: classes.multiInputContainer,
							input: classes.multiInputElement,
						}}
						onInputChanged={(item) => {
							setInputValue(item);
						}}
						onInputPaste={onInputPaste}
						onItemSelected={(item) => {
							setInputValue(item.value);
							setDestination([item]);
						}}
						queryFn={(e: string) =>
							searchContactsByPhone(e).then(mapContactsToSearchList)
						}
						outboundType={OutboundType.Call}
						isKeyDownHandled={false}
						disableSearchOnMobile={true}
						destinations={destination}
						showClearButton={true}
						onInputClear={onInputClear}
						disabled={!isViewEnabled}
					/>
				</Flex>
				<Dialpad
					onClick={dialpadClickCallback}
					destination={destination}
					isOutbound={true}
					isDisabled={!isViewEnabled}
				/>
				<div className={classes.callButtonContainer}>
					<button
						disabled={!isCallButtonActive}
						className={classes.initCallButton}
						children={
							<div className={classes.callButtonIconContainer}>
								<Call20Filled
									primaryFill={tokens.colorNeutralForegroundStaticInverted}
									className={classes.callButtonIcon}
								/>
								{intl.formatMessage({
									id: "Interaction.New.Call",
								})}
							</div>
						}
						onClick={onCallInitCallback}
					/>
				</div>
			</div>
		</div>
	);
};
